import { EventEmitter, Injectable, effect, inject, signal, untracked } from '@angular/core';
import { NgForm, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { productFiltersNames } from '@clubhub/constants';
import { AffordabilityPollResponse, IHolidayLetIncome, StepName } from '@clubhub/ignite/models/affordability';
import { ResultsView } from '@clubhub/ignite/models/affordability/enums';
import { IgniteResponse } from '@clubhub/ignite/models/ignite-response';
import { ProductsHistoryResponse } from '@clubhub/ignite/models/outcome-history';
import {
  IBridging,
  IMortgageRequirements,
  IRepaymentMethod,
  IUiCustomProductLength,
  IUiProductsSearchModel,
  ProductResultsConfigurationResponseModel,
} from '@clubhub/ignite/models/products';
import { PropertyModelRequest } from '@clubhub/ignite/models/property';
import { ProductsFilterHelper } from '@clubhub/ignite/services/products/products-filter/products-filter.helper';
import { ProductsIntuitiveOutcomeService } from '@clubhub/ignite/services/products/products-intuitive-outcome.service';
import { ProductsResultSettingsService } from '@clubhub/ignite/services/products/products-result-settings.service';
import { ProductsTransferService } from '@clubhub/ignite/services/products/products-transfer.service';
import { mockProductSearchRequest } from '@clubhub/ignite/testing/affordability/mock-product-search-request';
import { FormlyFormOptions } from '@ngx-formly/core';
import { SimpleGridComponent } from '@shared/components/simple-grid';
import { roles } from '@shared/constants/roles';
import { mapDateToUtc } from '@shared/helpers/date-helpers';
import {
  BridgingProduct,
  BtlType,
  CriteriaResponse,
  CustomProductCalculations, LenderAuditResult,
  LenderProductDetailsViewModel,
  LenderProductTransferViewModel,
  LendingTypeCode,
  MatchedLenderProducts,
  MortgageType,
  OutcomeDetails,
  OutcomeResponse,
  Product,
  ProductCollectionType,
  ProductLengthExtended,
  ProductTransferInfoModel,
  ProductType,
  ProductTypeString,
  ProductsApplicant,
  ProductsBridgingModelRequest,
  ProductsBtlModelRequest,
  ProductsResidentialModelRequest,
  PurchaserType,
  RepaymentMethod,
  SearchType,
} from '@shared/models';
import { LenderProduct } from '@shared/models/mi';
import {
  AuthorizeService,
  ClubHubDataService,
  IgniteHelperService,
  LendingTypeService,
  UserCustomProductSettingsService,
} from '@shared/services';
import { LoadingService } from '@shared/services/loading.service';
import { ProductService } from '@shared/services/product.service';
import sortBy from 'lodash-es/sortBy';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { v4 as newGuid } from 'uuid';
import { CriteriaV2Service } from '../criteria/criteria-v2.service';
import { IgniteCommonDataService } from '../ignite.store';
import { S365Service } from '../s365.service';
import { SavedSearchCommonDataService } from '../saved-searches/saved-search-common-data.service';
import { ProductsCalculationService } from './products-calculation.service';
import { ProductFilter, ProductsFilterService } from './products-filter.service';
import { ProductsSearchBtlService } from './products-search-btl.service';

@Injectable({
  providedIn: 'root',
})
export class ProductsSearchService {
  private readonly lendingTypeService = inject(LendingTypeService);
  private readonly dataService = inject<ClubHubDataService>(ClubHubDataService);
  private readonly authService = inject(AuthorizeService);
  private readonly router = inject(Router);
  private readonly productsFilterService = inject(ProductsFilterService);
  private readonly igniteCommonDataService = inject(IgniteCommonDataService);
  private readonly igniteHelperService = inject(IgniteHelperService);
  private readonly productsTransferService = inject(ProductsTransferService);
  private readonly productsIntuitiveOutcomeService = inject(ProductsIntuitiveOutcomeService);
  private readonly productsCalculationService = inject(ProductsCalculationService);
  private readonly loadingScreenService = inject(LoadingService);
  private readonly productsResultSettingsService = inject(ProductsResultSettingsService);
  private readonly s365Service = inject(S365Service);
  private readonly productsSearchBtlService = inject(ProductsSearchBtlService);
  private readonly productsFilterHelper = inject(ProductsFilterHelper);
  private readonly savedSearchCommonDataService = inject(SavedSearchCommonDataService);
  private readonly productService = inject(ProductService);
  private readonly criteriaV2Service = inject(CriteriaV2Service);

  public loading$ = new BehaviorSubject(false);
  public get loading() {
    return this.loading$.value;
  }

  public set loading(value: boolean) {
    this.loading$.next(value);
  }

  public allLoading$ = new BehaviorSubject(false);
  public get allLoading() {
    return this.allLoading$.value;
  }

  public set allLoading(value: boolean) {
    this.allLoading$.next(value);
  }

  public productResultsConfigLoading$ = new BehaviorSubject(false);

  public anyLoading$ = combineLatest([this.loading$, this.allLoading$, this.productResultsConfigLoading$]).pipe(
    map(([a, b, c]) => a || b || c),
  );

  public get anyLoading() {
    return this.loading$.value || this.allLoading$.value || this.productResultsConfigLoading$.value;
  }

  public customProductCalculations: CustomProductCalculations | undefined;
  public preserveCustomProductCalculations = false;
  public customProductCalculationsData$ = this.getCustomProductSettings();
  public form = new UntypedFormGroup({});
  public model: IUiProductsSearchModel | undefined;
  public options = {} as FormlyFormOptions;
  private readonly _currentStepName = signal<StepName | undefined>(undefined);
  public matchedProducts: MatchedLenderProducts[] | null = null;
  public matchedProductsFlat: Product[] | null = null;
  public productsAll$: Observable<Product[]> | null = null;
  public allProductsSnapshot = new BehaviorSubject<Product[] | null>(null);
  public matchedLenders: string[] = [];
  public productsModelRequest: ProductsResidentialModelRequest | ProductsBtlModelRequest | ProductsBridgingModelRequest | undefined =
    undefined;
  public lenderAuditResult: LenderAuditResult = LenderAuditResult.NotCompleted;
  public productsGrid: SimpleGridComponent | undefined;
  public resultsView: ResultsView = ResultsView.Details;
  public productsSearchId: string | undefined;
  public isAuditHistory: boolean = false;
  public requestHistory: unknown;
  public lenderList: string[] = [];
  public isOutcomeResults: boolean = false;
  public productReferencesToCompare = new Set<string>();
  public allProductsToCompare: Product[] = [];
  public hasPresetValue: boolean = false;
  public lenderProductTransferViewModel: LenderProductTransferViewModel | null = null;
  public productsSearchCompleted = new EventEmitter<void>();
  public productTransferModelSet = new EventEmitter<LenderProductTransferViewModel>();
  public productLoadingInProgress: boolean = false;
  public productsAndAffordabilityLoading: boolean = false;
  public ercRateReferenceDate: Date = new Date();
  private outcomeLoading: boolean = false;
  private readonly miProductsCount = 50;

  // For loaded outcomes, which lenders had matched products at save time (products may not actually exist in loaded
  // outcome results though, as only a subset are saved).
  public lendersWithMatchedProducts: Set<string> | null = null;

  public loadNewResultsConfiguration$ = new EventEmitter<[LendingTypeCode, ProductResultsConfigurationResponseModel]>();

  public constructor() {
    const userCustomProductSettingsService = inject(UserCustomProductSettingsService);

    this.reset();
    this.criteriaV2Service.outcomeUpdated.subscribe(() => {
      this.matchedProductsFlat = this.getLendersProductsFlat(null);
    });
    effect(() => {
      const result = this.igniteCommonDataService.results;
      untracked(() => {
        this.matchedProductsFlat = this.getLendersProductsFlat(result);
      });
    });

    // When the user updates their custom product settings, reset customProductCalculationsData$ so that next time it is
    // accessed it will fetch a fresh copy of the user custom settings.
    userCustomProductSettingsService.settingsChange.subscribe(() => {
      this.customProductCalculationsData$ = this.getCustomProductSettings();
    });
  }

  public get currentStepName(): StepName | undefined {
    return this._currentStepName();
  }

  public set currentStepName(value: StepName | undefined) {
    this._currentStepName.set(value);
  }

  public getProductsResidentialFormValue(): IUiProductsSearchModel {
    return this.form.value;
  }

  public get filteredProducts(): Product[] {
    return (this.productsGrid?.fullDatasource as Product[]) ?? [];
  }

  public get miProductsResult() {
    return (
      this.filteredProducts?.slice(0, this.miProductsCount).map(
        (r, index) =>
          ({
            productId: r.id,
            lenderName: r.lender,
            order: index,
            maximumAffordableLoanAmount: r.maximumAffordableLoanAmount,
            requiredMonthlyRentalIncome: r.requiredMonthlyRentalIncome,
          }) as LenderProduct,
      ) ?? ([] as LenderProduct[])
    );
  }

  public get productsOrder() {
    if (!this.productsGrid) {
      return null;
    }

    return {
      sortOn: this.productsGrid.sortOn,
      sortDir: this.productsGrid.sortDir,
    };
  }

  /**
   * Whether the broker's saved preferences have already been applied to the results filters on this search.
   *
   * Preferences are a starting point, not a rule: once applied, the filters are the broker's. Editing the search
   * or adding another tool searches again, and re-applying then would put back anything they had changed. Only
   * `reset` re-arms it - the new-search path - so "clear filters" does not bring the preferences back either.
   */
  public filterPreferencesApplied = false;

  /**
   * True only while a saved search is being replayed, from the moment its search starts until its saved filters
   * have been restored.
   *
   * Not the same as the `rerunSavedSearch` query parameter. "Edit inputs" lands on the form with that parameter
   * too, and it stays in the URL when the broker then presses Search - but that search comes from the form, not
   * from the saved request, and has nothing restored after it.
   */
  public isReplayingSavedSearch = false;

  /**
   * The search request the results filters were last filled from, or null before that has happened.
   *
   * Lets the save tell "the broker has not touched these filters" apart from "these filters have not been filled
   * in yet". Both look empty, and only the first should be saved as empty: `filterChanged` fires several times
   * on a new search before the request is reflected into the filters, and treating those early, empty states as
   * the broker's answer is what wiped Initial product period when this sync ran on every change.
   */
  public requestShownInFilters: object | null = null;

  /**
   * The search request as a saved search should store it: Rate type and Initial product period as the results
   * filters show them now, rather than as they were when the search ran.
   *
   * Only once the filters have been filled from this request - the form model is kept in step with them from that
   * point on. Before it, or when the request has since been replaced, the request is returned as it is.
   */
  public requestForSaving() {
    const request = this.productsModelRequest;
    const current = this.model?.mortgageRequirements;

    if (!request || !current || request !== this.requestShownInFilters) {
      return request;
    }

    return {
      ...request,
      productLengths: current.productLengths,
      rateTypes: current.rateTypes,
      productLengthFrom: current.customProductLength?.from,
      productLengthTo: current.customProductLength?.to,
    };
  }

  public reset(model: IUiProductsSearchModel | null = null) {
    this.preserveCustomProductCalculations = false;
    this.filterPreferencesApplied = false;
    this.ercRateReferenceDate = new Date();

    if (this.authService.isLoggedIn && !this.authService.historyView && !this.router.url.includes('outcomeHistory')) {
      this.customProductCalculationsData$.subscribe(customProductSettings => {
        if (!this.preserveCustomProductCalculations) {
          this.customProductCalculations = customProductSettings;
        }
      });
    }
    this.form.markAsUntouched();
    this.form = new UntypedFormGroup({});
    this.model = model ?? this.initializeModel();
    this.currentStepName = StepName.MortgageRequirements;
    if (this.options.resetModel) {
      this.options.resetModel();
    }
    this.productsFilterService.clearFilters();
    this.productsFilterService.clearSourcingFilters();
    this.productReferencesToCompare.clear();
    this.options = {};

    this.igniteCommonDataService.productModel = this.model;

    if (!!model) {
      this.model = model;
      this.form.reset(this.model);
    }
  }

  private getCustomProductSettings(): Observable<CustomProductCalculations> {
    return this.dataService.get<CustomProductCalculations>('CustomProductSettings/Users').pipe(shareReplay(1));
  }

  public resetProductTransfer(): void {
    this.lenderProductTransferViewModel = null;
  }

  public resetAllProducts(): void {
    this.productsAll$ = null;
  }

  public isComingSoonLender(lender: string): boolean {
    return ['BM Solutions'].includes(lender);
  }

  public resetParamsToDefault(): void {
    this.productsResultSettingsService.resetSort();
    this.igniteCommonDataService.productModel = null;
    this.igniteCommonDataService.propertyModel = null;
    this.productsSearchId = undefined;
    this.productsIntuitiveCriteriaId = null;
    this.matchedProducts = [];
    this.matchedProductsFlat = [];
    this.allProductsSnapshot = new BehaviorSubject<Product[] | null>(null);
    this.matchedLenders = [];
    this.productsModelRequest = undefined;
    this.lenderAuditResult = LenderAuditResult.NotCompleted;
    this.resultsView = ResultsView.Details;
    this.isAuditHistory = false;
    this.lenderList = [];
    this.productReferencesToCompare = new Set<string>();
    this.allProductsToCompare = [];
    this.resetProductTransfer();
  }

  public get isSecondChargeProducts(): boolean {
    return this.productsModelRequest?.mortgageType === MortgageType.SecondCharge;
  }

  public get productsToCompare(): (Product | Observable<Product[]>)[] {
    const matchedProducts = this.matchedProductsFlat;
    const allProducts = this.allProducts$;

    const prods = [...this.productReferencesToCompare].map(ref => {
      // Favour fetching the Product from the matched products first as this will have more accurate fee information
      // If not found AND the user has fetched all products, fallback to find the product in here (no need to check here
      // if the user has not fetched all products, since in that case they can only have selected a matched product)
      return (
        matchedProducts?.find(x => x.uniqueProductReference === ref) ??
        allProducts?.pipe(tap((products: Product[]) => products.find(x => x.uniqueProductReference === ref))) ??
        this.allProductsToCompare?.find(x => x.uniqueProductReference === ref)
      );
    });
    return prods;
  }

  public toggleProductComparison(product: Product): void {
    const ref = product.uniqueProductReference;
    if (this.productReferencesToCompare.has(ref)) {
      this.productReferencesToCompare.delete(ref);
      this.allProductsToCompare = this.allProductsToCompare.filter(product => product.uniqueProductReference !== ref);
    } else {
      this.productReferencesToCompare.add(ref);
      this.allProductsToCompare = [...this.allProductsToCompare, product];
    }
  }

  public isProductBeingCompared(product: Product): boolean {
    return this.productReferencesToCompare.has(product.uniqueProductReference);
  }

  public startLoadOutcomeHistory() {
    this.outcomeLoading = true;
    this.loadingScreenService.startLoading({ loading: true });
  }

  public stopLoadOutcomeHistory() {
    this.outcomeLoading = false;
    this.loadingScreenService.stopLoading({ loading: false });
  }

  public get outcomeHistoryLoading(): boolean {
    return this.outcomeLoading;
  }

  public get isBtlLendingType(): boolean {
    return this.productTypeCode?.toLowerCase() === 'btl';
  }

  public get isResLendingType(): boolean {
    return this.productTypeCode?.toLowerCase() === 'res';
  }

  public get productTypeCode(): string {
    return this.lendingTypeService.lendingType?.code ?? '';
  }

  public get productTypeString(): string {
    return this.lendingTypeService.lendingType?.name ?? '';
  }

  public get productTypeId(): number {
    return this.lendingTypeService.lendingType?.id ?? 0;
  }

  public get isLender() {
    return this.authService.hasRole(roles.lender);
  }

  public get isDetailsView() {
    return this.resultsView === ResultsView.Details;
  }

  public get isInputView() {
    return this.resultsView === ResultsView.Input;
  }

  public get isViewAllProductsView() {
    return this.resultsView === ResultsView.ViewAllProducts;
  }

  public get isComparedProductsView() {
    return this.resultsView === ResultsView.ComparedProducts;
  }

  public get hasComparedProducts(): boolean {
    return this.productReferencesToCompare.size > 0;
  }

  public get allProducts$() {
    return this.productsAll$ ?? (this.productsAll$ = this.getAllProducts());
  }

  public get comparedProducts(): (Product | undefined)[] {
    return [...this.productReferencesToCompare].map(
      ref =>
        this.matchedProductsFlat?.find(prod => prod.uniqueProductReference === ref) ??
        this.allProductsSnapshot.value?.find(prod => prod.uniqueProductReference === ref),
    );
  }

  public readonly mock = false;

  public get productsOnly(): boolean {
    return this.router.url.includes('products');
  }

  public get propertyValueDisabled(): boolean {
    return this.hasPresetValue && this.productsOnly;
  }

  public get loanAmountDisabled(): boolean {
    return this.hasPresetValue && this.productsOnly;
  }

  public get hasProducts(): boolean {
    return !!this.matchedProductsFlat?.length;
  }

  private initializeModel(): IUiProductsSearchModel {
    this.hasPresetValue = false;
    let propertyValue: number | null = null;
    let loanAmount: number | null = null;
    const purchasePriceShare: number | null = null;
    const discountedPurchasePrice: number | null = null;

    const propertyModel = this.igniteCommonDataService.propertyModel;
    if (this.productsOnly && propertyModel) {
      propertyValue = propertyModel.propertyDetails?.propertyValue ?? null;
      loanAmount = propertyModel.propertyDetails?.loanAmount ?? null;

      this.hasPresetValue = !!propertyValue && !!loanAmount;
    }

    return this.mock
      ? mockProductSearchRequest
      : ({
        mortgageRequirements: {
          propertyValue,
          purchasePriceShare,
          discountedPurchasePrice,
          loanAmount,
          mortgageLender: {
            value: null,
          },
          mortgageType: null,
          productTypeExtended: ProductType.Standard,
          newBuild: false,
          remortgageType: null,
          purchaserType: null,
          helpToBuy: null,
          repaymentMethod: {
            value: null,
            showInterestOnlyCriteria: null,
            showInterestOnlyPartAndPartCriteria: null,
          },
          repaymentVehicle: null,
          equityValue: null,
          interestOnlyAmount: null,
          mortgageTerm: {
            years: null,
            months: null,
          },
          btlType: this.lendingTypeService.isResLendingType ? null : BtlType.Standard,
          limitedCompanyPurchase: false,
          portfolioLandlord: false,
        },
      } as any);
  }

  public get criteriaLoadingInProgress(): boolean {
    return this.productsIntuitiveOutcomeService.criteriaLoadingInProgress;
  }

  public get productsIntuitiveCriteriaId(): string | null {
    return this.productsIntuitiveOutcomeService.criteriaSearchId;
  }

  public set productsIntuitiveCriteriaId(value: string | null) {
    this.productsIntuitiveOutcomeService.criteriaSearchId = value;
  }

  // Common for BTL and RES products
  private prepareLenderProducts(
    matchedLenderProductsObservable: Observable<MatchedLenderProducts[]>,
    fromAffordabilityProductsModel?: ProductsResidentialModelRequest | ProductsBtlModelRequest,
  ): Observable<MatchedLenderProducts[]> {
    return matchedLenderProductsObservable.pipe(
      map(results => {
        return results.map(lenderProduct => ({
          ...lenderProduct,
          get trueCostInitialPeriodFrom() {
            const trueCostInitialPeriods = lenderProduct.products
              ?.map(product => product.trueCostInitialPeriod)
              .filter(x => !!x) as number[];
            return trueCostInitialPeriods?.length ? Math.min(...trueCostInitialPeriods) : null;
          },
          get monthlyPaymentFrom() {
            const initialMonthlyPayment = lenderProduct.products
              ?.map(product => product.initialMonthlyPayment)
              .filter(x => !!x) as number[];
            return initialMonthlyPayment?.length ? Math.min(...initialMonthlyPayment) : null;
          },
        }));
      }),
      tap(this.createPackagerProductClones),
      switchMap(results => {
        const calculate = (results: MatchedLenderProducts[]) => {
          for (const lenderProduct of results) {
            if (lenderProduct.products) {
              lenderProduct.products.forEach(product => {
                product.brokerFees = this.getBrokerFees(product);
                product.assumedLegalFees = this.getAssumedLegalFees(product);
              });
              this.productsCalculationService.performCalculations(
                lenderProduct.products,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                fromAffordabilityProductsModel ?? this.productsModelRequest!,
                this.customProductCalculations?.deductCashback,
                this.customProductCalculations?.customFeeCalculationScenarios,
                this.getCustomFees(),
                this.customProductCalculations?.assumedLegalFeeCost ?? null,
                false,
                this.customProductCalculations?.customTrueCostPeriod ?? null,
                this.ercRateReferenceDate,
              );
            }
          }
        };

        for (const lenderProduct of results) {
          for (const product of lenderProduct.products ?? []) {
            product.lender = lenderProduct.lenderName;
          }
        }

        if (!this.productsModelRequest) {
          calculate(results);
          this.productsSearchCompleted.emit();
          return of(results);
        }

        const currentMortgageLender = this.productsModelRequest.currentMortgageLender || '';
        const mortgageType = this.productsModelRequest.mortgageType;

        const finish = () => {
          this.prepareMatchedProductResults(results, currentMortgageLender);
          calculate(results);
          this.productsSearchCompleted.emit();
          return results;
        };

        if (this.productsModelRequest.productType === LendingTypeCode.Bdg || mortgageType === undefined) {
          return of(finish());
        }

        // The product transfer lookup is an HTTP call that mutates the products, so the results must not be
        // emitted until it has completed. This previously ran in a detached subscribe(), which meant callers
        // awaiting getProducts() (the saved search rerun, for one) continued before matchedProducts had been
        // populated, and then applied saved fee settings to the *previous* search's products.
        return this.setProductTransferProducts(
          results,
          currentMortgageLender,
          this.productsModelRequest.productTransferInitialDate,
          mortgageType,
        ).pipe(map(() => finish()));
      }),
    );
  }

  public getProducts(
    scheduled: boolean,
    fromAffordabilityProductsModel?: ProductsResidentialModelRequest | ProductsBtlModelRequest,
    savedFeeSettings?: CustomProductCalculations,
  ): Observable<MatchedLenderProducts[]> {
    if (!this.productsModelRequest) {
      return of([]);
    }

    const feeSettings = savedFeeSettings ?? (this.preserveCustomProductCalculations ? this.customProductCalculations : undefined);

    // Always fetch custom product settings as they may have been changed in admin dashboard
    this.customProductCalculationsData$ = this.getCustomProductSettings();

    if (fromAffordabilityProductsModel?.productsSearchId) {
      this.productsSearchId = fromAffordabilityProductsModel.productsSearchId;
    }

    return this.customProductCalculationsData$.pipe(
      take(1),
      tap(customProductSettings => {
        this.customProductCalculations = feeSettings ?? customProductSettings;
      }),
      switchMap(() => {
        const getProductsObservable =
          this.productsModelRequest?.productType === LendingTypeCode.Btl
            ? this.productsSearchBtlService.getProducts(scheduled, this.productsModelRequest)
            : // Get RES products
            this.dataService.post<MatchedLenderProducts[]>(`Product/?scheduled=${scheduled}`, this.productsModelRequest);

        return this.prepareLenderProducts(getProductsObservable, fromAffordabilityProductsModel);
      }),
    );
  }

  public getProductsAndCriteria(
    scheduled: boolean,
    propertyModelRequest: PropertyModelRequest | null,
    fromAffordabilityProductsModel?: ProductsResidentialModelRequest | ProductsBtlModelRequest | ProductsBridgingModelRequest,
  ): Observable<[MatchedLenderProducts[], [OutcomeResponse[], OutcomeDetails]]> {
    this.handleProductsModel(fromAffordabilityProductsModel);
    this.loading = true;
    this.productLoadingInProgress = true;

    this.savedSearchCommonDataService.clearSavedSearch();

    const getIntuitiveOutcomeObservable = this.shouldLoadIntuitiveCriteria
      ? this.productsIntuitiveOutcomeService.getIntuitiveOutcome(
        this.productsModelRequest as ProductsResidentialModelRequest | ProductsBtlModelRequest,
        propertyModelRequest,
      )
      : of(null);

    const getProductsObservable = this.getProducts(scheduled, fromAffordabilityProductsModel);

    const resultsObservable = combineLatest([getProductsObservable, getIntuitiveOutcomeObservable]);

    this.igniteCommonDataService.searchToolsInUse.products = true;

    if (this.shouldLoadIntuitiveCriteria) {
      this.igniteCommonDataService.searchToolsInUse.criteria = true;
    }

    return resultsObservable;
  }

  private get isSecondChargeType() {
    const mortgageType =
      this.model?.mortgageRequirements?.mortgageType || this.igniteCommonDataService.affordabilityModel?.propertyAndLoan?.mortgageType;
    return mortgageType === MortgageType.SecondCharge;
  }

  public getMortgageType(): MortgageType {
    return this.model?.mortgageRequirements?.mortgageType || MortgageType.Purchase;
  }

  public getLendersProductsFlat(
    results: AffordabilityPollResponse | CriteriaResponse | null,
    { publishQuoteChanges = false } = {},
  ): Product[] {
    if (!this.matchedProducts) {
      return [];
    }

    let quotesChanged = false;

    this.matchedProducts.forEach(lenderProducts => {
      if (this.lendingTypeService.isBtlLendingType && this.isSecondChargeType) {
        lenderProducts.products?.forEach(p => (p.lenderAffordabilityMet = ''));
      }

      const outcomeResult = this.criteriaV2Service.getLenderOverallResultByLender(lenderProducts.lenderName);
      if (outcomeResult) {
        lenderProducts.outcomeResult = outcomeResult;
        lenderProducts.products?.forEach(p => (p.lenderOutcomeResult = outcomeResult));
      }
      const latestQuotesResult = this.igniteCommonDataService.results?.quotes || results?.quotes;
      if (latestQuotesResult) {
        this.updateResiProductMaxAffordableLoan(latestQuotesResult, lenderProducts);
        quotesChanged = this.updateLenderAffordabilityMet(latestQuotesResult, lenderProducts) || quotesChanged;
      }
    });

    // updateLenderAffordabilityMet writes BTL max loan / stress rate onto the quote objects in place, which the
    // OnPush lender rows cannot see. Re-publishing is opt-in because this also runs from inside the results
    // effect, where writing the signal would re-enter it - prepareMatchedProductResults is the caller that asks,
    // since that is the point where a product search has just landed and the quotes have gained new values.
    if (quotesChanged && publishQuoteChanges) {
      this.igniteCommonDataService.refreshResults();
    }

    return this.matchedProducts.flatMap(p => p.products ?? []);
  }

  public updateResiProductMaxAffordableLoan(quotes: IgniteResponse[] | null | undefined, lenderProducts: MatchedLenderProducts) {
    if (this.isBtlLendingType) {
      return;
    }

    lenderProducts.products.forEach(product => {
      const quote = quotes?.find(q => q.lenderName === product.lender);
      product.maximumAffordableLoanAmount = quote?.maximumAffordableLoanAmount ?? null;
    });
  }

  /** Returns true when a quote object was changed, so the caller can re-publish the results for the UI. */
  public updateLenderAffordabilityMet(quotes: IgniteResponse[] | null | undefined, lenderProducts: MatchedLenderProducts): boolean {
    const passValue = 'Pass';
    const failValue = 'Fail';
    let quoteChanged = false;

    if (quotes) {
      const result = quotes.find(x => x.lenderName === lenderProducts.lenderName);
      let isErrorResult =
        !result ||
        !!result?.httpError ||
        !!result?.error?.message ||
        !this.igniteCommonDataService.affordabilityLendersList.includes(result.lenderName);
      if (result) {
        isErrorResult = isErrorResult && !this.igniteCommonDataService.isNetFreePayTooLow(result);

        if (this.lendingTypeService.isBtlLendingType && this.igniteCommonDataService.affordabilityModel?.propertyAndLoan?.productLength) {
          const productLength = this.igniteCommonDataService.affordabilityModel.propertyAndLoan.productLength;
          let highestMaxAffordableLoan: number | undefined = undefined;
          let lowestStressRate: number | undefined = undefined;

          lenderProducts.products?.forEach(p => {
            // When doing BTL, the user is still able to use the initial period filters on the products grid, which
            // means that the server has had to send products of all periods, not just the ones that match the
            // affordability request. So, to determine the max loan and stress rate for affordability, we need to filter
            // them out based on initial period.
            const [lower, upper] = this.productsFilterHelper.getProductLengthBounds(productLength);
            if (!p.initialPeriod || p.initialPeriod < lower || p.initialPeriod > upper) {
              return;
            }

            if (typeof p.maximumAffordableLoanAmount === 'number') {
              highestMaxAffordableLoan = Math.max(highestMaxAffordableLoan ?? 0, p.maximumAffordableLoanAmount ?? 0);
            }

            if (typeof p.stressRate === 'number') {
              lowestStressRate = Math.min(lowestStressRate ?? Number.MAX_VALUE, p.stressRate);
            }
          });

          // Only set max loan/stress rate if they're not already set (They will be when loading from outcome history)
          if (typeof result.maximumAffordableLoanAmount !== 'number' && typeof highestMaxAffordableLoan === 'number') {
            result.maximumAffordableLoanAmount = result.maximumValue = highestMaxAffordableLoan;
            quoteChanged = true;
          }

          if (typeof result.stressRate !== 'number' && typeof lowestStressRate === 'number') {
            result.stressRate = lowestStressRate;
            quoteChanged = true;
          }

          return quoteChanged;
        }

        // Residential products has different logic for "AffordabilityMet"
        const affordabilityMetValue = this.igniteCommonDataService.isAffordabilityMet(result)
          ? passValue
          : failValue;
        const lenderAffordabilityMet = isErrorResult
          ? null
          : affordabilityMetValue;

        lenderProducts.products?.forEach(p => (p.lenderAffordabilityMet = lenderAffordabilityMet ?? ''));
      }
    }

    return quoteChanged;
  }

  private prepareMatchedProductResults(results: MatchedLenderProducts[], currentMortgageLender: string): void {
    results.forEach(lender => {
      if (lender.lenderName === 'Leeds') {
        lender.products?.forEach(product => {
          if (product.initialMonthlyPayment === null) {
            // Leeds return aprc but we don't want to show this unless standard calcs are populated
            product.aprc = null;
          }
        });
      }
    });
    this.matchedLenders = sortBy([
      ...new Set(
        results
          .filter(lender => lender.products)
          .map(product => {
            return product.lenderName;
          }),
      ),
    ]);

    this.matchedProducts = results;
    this.matchedProductsFlat = this.getLendersProductsFlat(null, { publishQuoteChanges: true });

    this.matchedProductsFlat = this.excludeProductTransferProducts(this.matchedProductsFlat, currentMortgageLender);

    this.loading = false;
    this.productLoadingInProgress = false;
  }

  public excludeProductTransferProducts(products: Product[], currentMortgageLender: string): Product[] {
    // Mark all productTransfer products and exclude from matchedProductsFlat
    return this.productsTransferService.filterProductTransferProducts(products, currentMortgageLender);
  }

  public setProductTransferProducts(
    lendersProducts: MatchedLenderProducts[],
    currentMortgageLender: string,
    productTransferInitialDate: Date | string | undefined,
    mortgageType: MortgageType,
  ): Observable<LenderProductTransferViewModel | null> {
    return this.productsTransferService
      .getProductTransferProducts(lendersProducts, currentMortgageLender, productTransferInitialDate, mortgageType)
      .pipe(
        tap((result: LenderProductTransferViewModel | null) => {
          this.lenderProductTransferViewModel = result;
          if (result) {
            this.productTransferModelSet.emit(result);
          }
        }),
      );
  }

  private handleProductsModel(fromAffordabilityProductsModel?: ProductsResidentialModelRequest | ProductsBtlModelRequest): void {
    if (fromAffordabilityProductsModel && this.model?.mortgageRequirements.mortgageTerm) {
      Object.assign(this.model.mortgageRequirements, fromAffordabilityProductsModel);
      this.model.mortgageRequirements.mortgageTerm.months = fromAffordabilityProductsModel.mortgageTermMonths;
      this.model.mortgageRequirements.mortgageTerm.years = fromAffordabilityProductsModel.mortgageTermYears;
    }
    this.productsSearchId = newGuid();
    this.productsModelRequest = this.mappedModel(this.form.valid && !this.form.pristine ? this.form.value : this.model);

    if (this.productsModelRequest) {
      this.productsFilterService.productsFilterContext = this.productsModelRequest;
      this.productsFilterService.setProductFilterDefinitions();
      this.productsModelRequest.repaymentMethod = this.adjustRepaymentMethod(this.productsModelRequest.repaymentMethod);
    }
  }

  public adjustRepaymentMethod(repaymentMethod: number | IRepaymentMethod | undefined): number | undefined {
    // this is due to mapping from affordability to product
    return typeof repaymentMethod === 'number' ? repaymentMethod : repaymentMethod?.value;
  }

  private updateBaseFormFields(updatedFormValues: any) {
    if (updatedFormValues && !!this.form.value.mortgageRequirements) {
      this.form.value.mortgageRequirements.years = updatedFormValues.years;
      this.form.value.mortgageRequirements.months = updatedFormValues.months;
      this.form.value.mortgageRequirements.mortgageTerm.years = updatedFormValues.years;
      this.form.value.mortgageRequirements.mortgageTerm.months = updatedFormValues.months;
      this.form.value.mortgageRequirements.discountedPurchasePrice = updatedFormValues.discountedPurchasePrice;
      this.form.value.mortgageRequirements.loanAmount = updatedFormValues.loanAmount;
      this.form.value.mortgageRequirements.propertyValue = updatedFormValues.propertyValue;
      this.form.value.mortgageRequirements.purchasePriceShare = updatedFormValues.purchasePriceShare;
      this.form.value.mortgageRequirements.repaymentMethod.value = updatedFormValues.repaymentMethod;
      if (updatedFormValues.repaymentMethod?.value === RepaymentMethod.InterestOnlyPartAndPart) {
        this.form.value.mortgageRequirements.interestOnlyAmount = updatedFormValues.interestOnlyAmount;
      }
    }
  }

  /**
   * The edit search form has only a few fields.
   * Use a different mapping function to avoid overwriting existing values with empty ones.
   */
  public mapEditSearchFormToModel(model = {}, form?: NgForm) {
    this.model = { ...this.model, ...model } as IUiProductsSearchModel;
    this.productsModelRequest = this.mappedModel(this.model);
    const formValues = form?.form?.getRawValue();

    if (formValues && !!this.form.value.bridging) {
      this.form.value.bridging.loanAmount = formValues.loanAmount;
      this.form.value.bridging.propertyValue = formValues.propertyValue;
      this.form.value.bridging.loanTermMonths = formValues.months;
      this.form.value.bridging.methodOfRepayment = formValues.repaymentMethod;
    }

    if (formValues && this.isBtlLendingType) {
      this.form.value.mortgageRequirements.rentalAmountReceivedExpected = formValues.rentalIncome;
    }

    if (formValues && !!this.form.value.mortgageRequirements) {
      this.updateBaseFormFields(formValues);
    }
  }

  public updateModel(model = {}, form?: NgForm) {
    this.model = { ...this.model, ...model } as IUiProductsSearchModel;
    this.productsModelRequest = this.mappedModel(this.model);
    const formValues = form?.form?.getRawValue();

    if (formValues && !!this.form.value.bridging) {
      this.form.value.bridging.loanAmount = formValues.loanAmount;
      this.form.value.bridging.propertyValue = formValues.propertyValue;
      this.form.value.bridging.loanTermMonths = formValues.months;
      this.form.value.bridging.methodOfRepayment = formValues.repaymentMethod;
    }

    if (formValues && !!this.form.value.mortgageRequirements) {
      this.updateBaseFormFields(formValues);
      this.form.value.mortgageRequirements.additionalBorrowing = formValues.additionalBorrowing;
      this.form.value.mortgageRequirements.capitalRaising = formValues.capitalRaising;
      this.form.value.mortgageRequirements.amountTransferredFromOtherLender = formValues.amountTransferredFromOtherLender;
    }
  }

  public updateModelFromSF(model: ProductsResidentialModelRequest | ProductsBtlModelRequest) {
    if (this.model?.mortgageRequirements) {
      this.model.mortgageRequirements.loanAmount = model.loanAmount;
      if (this.model.mortgageRequirements.mortgageTerm) {
        this.model.mortgageRequirements.mortgageTerm.years = model.mortgageTermYears;
        this.model.mortgageRequirements.mortgageTerm.months = model.mortgageTermMonths;
      }
      this.model.mortgageRequirements.propertyValue = model.propertyValue;
      if (model.mortgageType !== undefined) {
        this.model.mortgageRequirements.mortgageType = model.mortgageType;
      }
      this.model.mortgageRequirements.helpToBuy = (model as ProductsResidentialModelRequest).helpToBuy;
      this.model.mortgageRequirements.purchaserType = model.purchaserType;
      if (model.productType === LendingTypeCode.Btl) {
        this.model.mortgageRequirements.rentalAmountReceivedExpected = (model as ProductsBtlModelRequest).rentalAmountReceivedExpected;
      }
      this.model.mortgageRequirements.repaymentMethod = {
        value: model.repaymentMethod,
        showInterestOnlyCriteria: false,
        showInterestOnlyPartAndPartCriteria: false,
      };
      if (model.repaymentMethod === RepaymentMethod.InterestOnlyPartAndPart) {
        this.model.mortgageRequirements.interestOnlyAmount = model.interestOnlyAmount;
      }
      if (model.repaymentMethod === RepaymentMethod.InterestOnlyPartAndPart || model.repaymentMethod === RepaymentMethod.InterestOnly) {
        this.model.mortgageRequirements.repaymentVehicle = (model as ProductsResidentialModelRequest).repaymentVehicle;
        this.model.mortgageRequirements.equityValue = (model as ProductsResidentialModelRequest).equityValue;
      }
      if (model.mortgageType === MortgageType.Remortgage) {
        this.model.mortgageRequirements.currentMortgageLender = model.currentMortgageLender;
        if (model.productTransferInitialDate) {
          const productTransferInitialDate = this.igniteHelperService.dateToDateInput(model.productTransferInitialDate);
          if (productTransferInitialDate) {
            this.model.mortgageRequirements.productTransferInitialDate = productTransferInitialDate;
          }
        }
      }
    }
  }

  public reloadAllProducts(): void {
    this.productsAll$ = this.getAllProducts();
  }

  private prepareAllProducts(allProductsObservable: Observable<Product[]>) {
    return allProductsObservable.pipe(
      tap(() => this.allLoading = false),
      map(products => {
        if (this.lenderProductTransferViewModel) {
          return this.productsTransferService.filterProductTransferProducts(products, this.productsModelRequest?.currentMortgageLender);
        }

        return products;
      }),
      tap(products => {
        products.forEach(product => {
          product.brokerFees = this.getBrokerFees(product);
          product.assumedLegalFees = this.getAssumedLegalFees(product);
        });
        this.productsCalculationService.performCalculations(
          products,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          this.productsModelRequest!,
          this.customProductCalculations?.deductCashback,
          this.customProductCalculations?.customFeeCalculationScenarios,
          this.getCustomFees(),
          this.customProductCalculations?.assumedLegalFeeCost ?? null,
          false,
          this.customProductCalculations?.customTrueCostPeriod ?? null,
        );

        this.allProductsSnapshot.next(products);
      }),
    );
  }

  private getAllProducts(): Observable<Product[]> {
    if (!this.productsModelRequest) {
      return of([]);
    }

    this.allLoading = true;
    this.productsSearchId = newGuid();
    this.productsModelRequest = this.mappedModel(this.form.valid && !this.form.pristine ? this.form.value : this.model);

    const isBtl = this.productsModelRequest?.productType === LendingTypeCode.Btl;
    const isBdg = this.productsModelRequest?.productType === LendingTypeCode.Bdg;
    const productCollectionType = isBdg
      ? ProductCollectionType.Bridging
      : ProductCollectionType.ResiBtl;
    const productsEndpoint = isBdg
      ? 'Bridging'
      : 'ResiBtl';

    const nonBtlProductsObservable = this.isLender
      ? this.productService.getAllProducts(productCollectionType, 1).pipe(map(response => response.products))
      : this.dataService.post<Product[]>(`product/GetAllProducts/${productsEndpoint}`, this.productsModelRequest);

    const allProductsObservable = isBtl
      ? this.productsSearchBtlService.getAllProducts(this.productsModelRequest as ProductsBtlModelRequest)
      : nonBtlProductsObservable;

    return this.prepareAllProducts(allProductsObservable as Observable<Product[]>);
  }

  public getLoanAmount(): number | undefined {
    return this.productsModelRequest?.loanAmount;
  }

  public getMortgageTerm(): string | undefined {
    const mortgageTerm: number | undefined = this.productsModelRequest?.mortgageTermYears;
    if (mortgageTerm) {
      const length: string = mortgageTerm > 1 ? 'years' : 'year';
      return `${mortgageTerm} ${length}`;
    }
  }

  public getRepaymentMethod(): string | undefined {
    switch (this.productsModelRequest?.repaymentMethod) {
      case RepaymentMethod.CapitalAndInterest:
        return 'Capital and Interest';
      case RepaymentMethod.InterestOnly:
        return 'Interest Only';
      case RepaymentMethod.InterestOnlyPartAndPart:
        return 'Interest Only Part and Part';
    }
  }

  public resetProducts(): void {
    this.resetAllProducts();
    this.allProductsSnapshot.next(null);
    this.matchedProducts = null;
    this.matchedProductsFlat = null;
    this.productReferencesToCompare.clear();
    this.isOutcomeResults = false;
    this.resetProductTransfer();
    this.lendersWithMatchedProducts = null;
  }

  public setProductSearchRequestModel(uiModel: IUiProductsSearchModel) {
    this.model = uiModel;
    this.igniteCommonDataService.productModel = uiModel;
    this.productsModelRequest = this.mappedModel(uiModel);
    return this.productsModelRequest;
  }

  private mappedModel(uiModel: IUiProductsSearchModel) {
    switch (this.productTypeCode.toUpperCase()) {
      case LendingTypeCode.Res:
        return this.mappedModelResi(uiModel);
      case LendingTypeCode.Btl:
        return this.mappedModelBtl(uiModel);
      case LendingTypeCode.Bdg:
        return this.mappedModelBdg(uiModel);
      default:
        return;
    }
  }

  private mappedModelBdg(uiModel: IUiProductsSearchModel): ProductsBridgingModelRequest {
    const { bridging: bridging } = uiModel;
    return {
      productsSearchId: this.productsSearchId,
      productType: LendingTypeCode.Bdg,
      regulated: bridging.regulated,
      methodOfRepayment: bridging.methodOfRepayment,
      propertyValue: bridging.propertyValue,
      limitedCompany: bridging.limitedCompany,
      currentMortgageBalanceOutstanding: bridging.currentMortgageBalanceOutstanding,
      loanSecurity: bridging.loanSecurity,
      loanAmount: bridging.loanAmount,
      loanTerm: bridging.loanTermMonths,
    } as ProductsBridgingModelRequest;
  }

  private mappedModelResi(uiModel: IUiProductsSearchModel): ProductsResidentialModelRequest {
    const { mortgageRequirements: mortgageRequirements } = uiModel;
    return {
      productsSearchId: this.productsSearchId,
      caseId: this.s365Service.s365Data?.caseId,
      productType: LendingTypeCode.Res,
      productTypeExtended: mortgageRequirements.productTypeExtended,
      newBuild: mortgageRequirements.newBuild,
      currentMortgageLender: this.mapCurrentMortgageLender(uiModel.mortgageRequirements),
      productTransferInitialDate: this.igniteHelperService.getDateOrNull(uiModel.mortgageRequirements.productTransferInitialDate),
      mortgageType: mortgageRequirements.mortgageType,
      remortgageType: mortgageRequirements.remortgageType,
      propertyValue: mortgageRequirements.propertyValue,
      propertyType: mortgageRequirements.propertyType,
      loanAmount: mortgageRequirements.loanAmount,
      outstandingBalanceCurrentMortgage: mortgageRequirements.outstandingBalanceCurrentMortgage,
      helpToBuy: mortgageRequirements.helpToBuy,
      repaymentMethod: this.adjustRepaymentMethod(mortgageRequirements.repaymentMethod),
      equityValue: mortgageRequirements.equityValue,
      repaymentVehicle: mortgageRequirements.repaymentVehicle,
      mortgageTermYears: mortgageRequirements.mortgageTerm?.years,
      mortgageTermMonths: mortgageRequirements.mortgageTerm?.months ?? 0,
      interestOnlyAmount: mortgageRequirements.interestOnlyAmount,
      productLengths: mortgageRequirements.productLengths,
      rateTypes: mortgageRequirements.rateTypes,
      purchasePriceShare: mortgageRequirements.purchasePriceShare,
      additionalBorrowing: mortgageRequirements.additionalBorrowing,
      amountTransferredFromOtherLender: mortgageRequirements.amountTransferredFromOtherLender,
      capitalRaising: mortgageRequirements.capitalRaising?.value,
      discountedPurchasePrice: mortgageRequirements.discountedPurchasePrice,
      searchType: SearchType.Product,
      firstTimeBuyer: mortgageRequirements.purchaserType ? mortgageRequirements.purchaserType === PurchaserType.FirstTimeBuyer : null,
      productLengthFrom: mortgageRequirements.customProductLength?.from,
      productLengthTo: mortgageRequirements.customProductLength?.to,
      numberOfApplicants: mortgageRequirements.applicants?.numberOfApplicants,
      applicants: mortgageRequirements.applicants?.applicantAges?.map((applicantAge, index) => {
        return {
          applicantNumber: applicantAge.applicantNumber ?? index + 1,
          dateOfBirth: applicantAge.dateOfBirth?.year ? mapDateToUtc(applicantAge.dateOfBirth) : null,
        };
      }),
      lts: mortgageRequirements.lts,
      ltv: mortgageRequirements.ltv,
      ltvDiscountedPrice: mortgageRequirements.ltvDiscountedPrice,
    } as ProductsResidentialModelRequest;
  }

  private mapTriStateToBoolean(value: number | undefined): boolean | undefined {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
    return undefined;
  }

  private mappedModelBtl(uiModel: IUiProductsSearchModel): ProductsBtlModelRequest {
    const { mortgageRequirements: mortgageRequirements } = uiModel;

    return {
      productsSearchId: this.productsSearchId,
      caseId: this.s365Service.s365Data?.caseId,
      productType: LendingTypeCode.Btl,
      newBuild: mortgageRequirements.newBuild,
      mortgageType: mortgageRequirements.mortgageType,
      currentMortgageLender: this.mapCurrentMortgageLender(uiModel.mortgageRequirements),
      productTransferInitialDate: this.igniteHelperService.getDateOrNull(uiModel.mortgageRequirements.productTransferInitialDate),
      remortgageType: mortgageRequirements.remortgageType,
      propertyValue: mortgageRequirements.propertyValue,
      propertyType: mortgageRequirements.propertyType,
      loanAmount: mortgageRequirements.loanAmount,
      outstandingBalanceCurrentMortgage: mortgageRequirements.outstandingBalanceCurrentMortgage,
      purchaserType: mortgageRequirements.purchaserType,
      repaymentMethod: this.adjustRepaymentMethod(mortgageRequirements.repaymentMethod),
      mortgageTermYears: mortgageRequirements.mortgageTerm?.years,
      mortgageTermMonths: mortgageRequirements.mortgageTerm?.months ?? 0,
      limitedCompanyPurchase: mortgageRequirements.limitedCompanyPurchase,
      limitedCompanySpecialPurposeVehicle: mortgageRequirements.limitedCompanySpecialPurposeVehicle,
      portfolioLandlord: mortgageRequirements.portfolioLandlord,
      numberOfProperties: mortgageRequirements.numberOfProperties,
      location: mortgageRequirements.location,
      btlType: mortgageRequirements.btlType,
      numberOfBedrooms: mortgageRequirements.numberOfBedrooms,
      numberOfBlocks: mortgageRequirements.numberOfBlocks,
      propertySelfContainedFlats: mortgageRequirements.propertySelfContainedFlats,
      firstTimeBuyer: this.mapTriStateToBoolean(mortgageRequirements.firstTimeBuyer),
      firstTimeLandlord: this.mapTriStateToBoolean(mortgageRequirements.firstTimeLandlord),
      ownerOccupier: this.mapTriStateToBoolean(mortgageRequirements.ownerOccupier),
      holidayLetIncomeLow: mortgageRequirements.holidayLetIncome?.holidayLetIncomeLow,
      holidayLetIncomeMedium: mortgageRequirements.holidayLetIncome?.holidayLetIncomeMedium,
      holidayLetIncomeHigh: mortgageRequirements.holidayLetIncome?.holidayLetIncomeHigh,
      holidayLetIncomeFrequency: mortgageRequirements.holidayLetIncome?.holidayLetIncomeFrequency,
      rentalAmountReceivedExpected: mortgageRequirements.rentalAmountReceivedExpected,
      interestOnlyAmount: mortgageRequirements.interestOnlyAmount,
      productLengths: mortgageRequirements.productLengths,
      rateTypes: mortgageRequirements.rateTypes,
      searchType: SearchType.Product,
      // Default value for BTL
      productTypeExtended: ProductType.Standard,
      productLengthFrom: mortgageRequirements.customProductLength?.from,
      productLengthTo: mortgageRequirements.customProductLength?.to,
      numberOfApplicants: mortgageRequirements.applicants?.numberOfApplicants,
      applicants: mortgageRequirements.applicants?.applicantAges?.map((applicantAge, index) => {
        return {
          applicantNumber: applicantAge.applicantNumber ?? index + 1,
          dateOfBirth: applicantAge.dateOfBirth?.year ? mapDateToUtc(applicantAge.dateOfBirth) : null,
          countryResident: applicantAge.countryResident,
          grossAnnualIncome: applicantAge.grossAnnualIncome,
          taxStatusBtl: applicantAge.taxStatusBtl,
        } satisfies ProductsApplicant;
      }),
    } as ProductsBtlModelRequest;
  }

  public set auditResultsStatusChange(status: LenderAuditResult) {
    this.lenderAuditResult = status;
  }

  public getPreviousOutcome(productsSearchId: string, includeCriteria: boolean, outcomeHistoryId: string | null) {
    return this.dataService.get<any>(
      `Product/outcome/${productsSearchId}?includeCriteria=${includeCriteria}&outcomeHistoryId=${outcomeHistoryId ?? ''}`,
      { loading: false },
    );
  }

  public getProductTransferDetails(lenderName: string): Observable<ProductTransferInfoModel> {
    return this.productsTransferService.getProductTransferDetails(lenderName);
  }

  private mapCurrentMortgageLender(model: IMortgageRequirements) {
    return this.igniteHelperService.getCurrentMortgageLender(model.currentMortgageLender, model.mortgageLender);
  }

  public processHistoricProducts(result: ProductsHistoryResponse) {
    this.startLoadOutcomeHistory();
    const request = JSON.parse(result.requestJson);
    if (request) {
      // MCONE-4953: Force set NULL to prevent zero value. (loanAmount and propertyValue can't be Zero)
      request.loanAmount = request.loanAmount ?? null;
      request.propertyValue = request.propertyValue ?? null;
      request.mortgageTermMonths = request.mortgageTermMonths ?? null;
      request.mortgageTermYears = request.mortgageTermYears ?? null;
      request.purchasePriceShare = request.purchasePriceShare ?? null;
      request.discountedPurchasePrice = request.discountedPurchasePrice ?? null;
    }

    this.productsModelRequest = request;

    const response = JSON.parse(result.responseJson);
    if (response.products) {
      this.matchedProducts = response.products;
    } else if (result.responseJson) {
      const products = JSON.parse(result.responseJson);
      this.matchedProducts = products;
    }

    if (this.matchedProducts) {
      this.createPackagerProductClones(this.matchedProducts);
      for (const lenderProduct of this.matchedProducts) {
        if (lenderProduct.products) {
          lenderProduct.products.forEach(product => {
            product.brokerFees = this.getBrokerFees(product);
            product.assumedLegalFees = this.getAssumedLegalFees(product);
          });
          this.productsCalculationService.performCalculations(
            lenderProduct.products,

            request,
            this.customProductCalculations?.deductCashback,
            this.customProductCalculations?.customFeeCalculationScenarios,
            this.getCustomFees(),
            this.customProductCalculations?.assumedLegalFeeCost ?? null,
            false,
            this.customProductCalculations?.customTrueCostPeriod ?? null,
          );
        }
        for (const product of lenderProduct.products ?? []) {
          product.lender = lenderProduct.lenderName;

          product.lenderLogoUrl = lenderProduct.lenderLogoUrl;
        }
      }
      this.matchedProducts = this.matchedProducts.map(lenderProduct => ({
        ...lenderProduct,
        get trueCostInitialPeriodFrom() {
          const trueCostInitialPeriods = lenderProduct.products?.map(product => product.trueCostInitialPeriod).filter(x => !!x) as number[];
          return trueCostInitialPeriods?.length ? Math.min(...trueCostInitialPeriods) : null;
        },
        get monthlyPaymentFrom() {
          const initialMonthlyPayment = lenderProduct.products?.map(product => product.initialMonthlyPayment).filter(x => !!x) as number[];
          return initialMonthlyPayment?.length ? Math.min(...initialMonthlyPayment) : null;
        },
      }));

      this.matchedProductsFlat = this.getLendersProductsFlat(null);
    }

    if (this.matchedProductsFlat) {
      this.matchedProductsFlat = this.excludeProductTransferProducts(this.matchedProductsFlat, request.currentMortgageLender);
    }

    const mortgageRequirements = {
      mortgageLender: !!request.currentMortgageLender ? { value: request.currentMortgageLender, label: request.currentMortgageLender } : {},
      currentMortgageLender: request.currentMortgageLender,
      productTransferInitialDate: this.igniteHelperService.dateToDateInput(request.productTransferInitialDate),
      currentMortgageBalance: request.currentMortgageBalance,
      mortgageType: request.mortgageType,
      productTypeExtended: request.productTypeExtended,
      btlType: request.btlType,
      helpToBuy: request.helpToBuy,
      limitedCompanyPurchase: request.limitedCompanyPurchase,
      loanAmount: request.loanAmount,
      mortgageTerm: { months: request.mortgageTermMonths, years: request.mortgageTermYears },
      portfolioLandlord: request.portfolioLandlord,
      productType: request.productType,
      propertyValue: request.propertyValue,
      purchaserType: request.purchaserType,
      remortgageType: request.remortgageType,
      repaymentMethod: { value: request.repaymentMethod } as IRepaymentMethod,
      equityValue: request.equityValue,
      repaymentVehicle: request.repaymentVehicle,
      purchasePriceShare: request.purchasePriceShare,
      discountedPurchasePrice: request.discountedPurchasePrice,
      additionalBorrowing: request.additionalBorrowing,
      amountTransferredFromOtherLender: request.amountTransferredFromOtherLender,
      capitalRaising: request.capitalRaising,
      newBuild: request.newBuild ?? false,
      productLengths: request.productLengths,
      rateTypes: request.rateTypes,
      propertyType: request.propertyType,
      dateOfBirth: this.igniteHelperService.dateToDateInput(request.dateOfBirth),
      applicants: this.getApplicantsFromRequest(request),
      customProductLength: { from: request.productLengthFrom, to: request.productLengthTo } as IUiCustomProductLength,
      firstTimeBuyer: request.firstTimeBuyer,
      firstTimeLandlord: request.firstTimeLandlord,
      ownerOccupier: request.ownerOccupier,
      rentalAmountReceivedExpected: request.rentalAmountReceivedExpected,
      location: request.location,
      numberOfBlocks: request.numberOfBlocks,
      numberOfBedrooms: request.numberOfBedrooms,
      holidayLetIncome: request.holidayLetIncome,
    } as IUiProductsSearchModel['mortgageRequirements'];

    const bridging = {
      regulated: request.regulated,
      propertyValue: request.propertyValue,
      loanAmount: request.loanAmount,
      loanSecurity: request.loanSecurity,
      currentMortgageBalanceOutstanding: request.currentMortgageBalanceOutstanding,
      loanTermMonths: request.loanTerm,
      methodOfRepayment: request.methodOfRepayment,
      limitedCompany: request.limitedCompany,
    } as IBridging;

    this.setProductTransferProducts(
      this.matchedProducts ?? [],
      mortgageRequirements.currentMortgageLender ?? '',
      request.productTransferInitialDate,
      request.mortgageType,
    ).subscribe(() => {
      this.requestHistory = mortgageRequirements;
      this.reset({ mortgageRequirements, bridging } as IUiProductsSearchModel);
      this.isOutcomeResults = true;
      this.resultsView = ResultsView.MatchedProducts;
      this.currentStepName = StepName.Results;

      setTimeout(() => {
        this.productsFilterService.applySavedFilters(result.miProductsFilters);
      }, 0);
      this.loading = false;
    });

    this.loadNewResultsConfiguration$.emit([result.lendingTypeCode as LendingTypeCode, result.productResultsConfiguration]);

    this.resultsView = ResultsView.MatchedProducts;
    this.stopLoadOutcomeHistory();
  }

  private getApplicantsFromRequest(request: any) {
    return {
      numberOfApplicants: request.numberOfApplicants,
      applicantAges: request.applicants?.map((r: any) => {
        return {
          applicantNumber: r.applicantNumber,
          dateOfBirth: this.igniteHelperService.dateToDateInput(r.dateOfBirth),
          countryResident: r.countryResident,
          grossAnnualIncome: r.grossAnnualIncome,
          taxStatusBtl: r.taxStatusBtl,
        };
      }),
    };
  }

  public processHistoricData(result: ProductsHistoryResponse) {
    this.startLoadOutcomeHistory();
    const request = JSON.parse(result.requestJson);

    const { mortgageRequirements, bridging } = this.getUiModelFromRequestModel(request);

    this.requestHistory = mortgageRequirements;
    this.reset({ mortgageRequirements, bridging } as IUiProductsSearchModel);
    this.productsModelRequest = request;
    this.isOutcomeResults = true;
    this.resultsView = ResultsView.MatchedProducts;
    this.currentStepName = StepName.Results;
    this.loading = false;
    this.productsSearchId = request.productsSearchId;
    this.lendersWithMatchedProducts = result.lendersWithMatchedProducts ? new Set(result.lendersWithMatchedProducts) : null;

    this.resultsView = ResultsView.MatchedProducts;

    this.productsFilterService.applySavedFilters(result.miProductsFilters);
    this.stopLoadOutcomeHistory();
  }

  public getUiModelFromRequestModel(request: any) {
    if (request) {
      // MCONE-4953: Force set NULL to prevent zero value. (loanAmount and propertyValue can't be Zero)
      request.loanAmount = request.loanAmount ?? null;
      request.propertyValue = request.propertyValue ?? null;
      request.mortgageTermMonths = request.mortgageTermMonths ?? null;
      request.mortgageTermYears = request.mortgageTermYears ?? null;
      request.purchasePriceShare = request.purchasePriceShare ?? null;
      request.discountedPurchasePrice = request.discountedPurchasePrice ?? null;
      request.interestOnlyAmount = request.interestOnlyAmount ?? null;
    }

    let purchaserType: PurchaserType | null;
    switch (request.firsTimeBuyer) {
      case true:
        purchaserType = PurchaserType.FirstTimeBuyer;
        break;
      case false:
        purchaserType = PurchaserType.HomeMover;
        break;
      default:
        purchaserType = null;
    }

    const mortgageRequirements = {
      mortgageLender: !!request.currentMortgageLender ? { value: request.currentMortgageLender, label: request.currentMortgageLender } : {},
      currentMortgageLender: request.currentMortgageLender,
      productTransferInitialDate: this.igniteHelperService.dateToDateInput(request.productTransferInitialDate),
      currentMortgageBalance: request.currentMortgageBalance,
      mortgageType: request.mortgageType,
      productTypeExtended: request.productTypeExtended,
      btlType: request.btlType,
      helpToBuy: request.helpToBuy,
      limitedCompanyPurchase: request.limitedCompanyPurchase,
      loanAmount: request.loanAmount,
      mortgageTerm: { months: request.mortgageTermMonths, years: request.mortgageTermYears },
      portfolioLandlord: request.portfolioLandlord,
      productType: request.productType,
      propertyValue: request.propertyValue,
      purchaserType,
      remortgageType: request.remortgageType,
      repaymentMethod: { value: request.repaymentMethod } as IRepaymentMethod,
      equityValue: request.equityValue,
      interestOnlyAmount: request.interestOnlyAmount,
      repaymentVehicle: request.repaymentVehicle,
      newBuild: request.newBuild ?? false,
      purchasePriceShare: request.purchasePriceShare,
      additionalBorrowing: request.additionalBorrowing,
      amountTransferredFromOtherLender: request.amountTransferredFromOtherLender,
      capitalRaising: request.capitalRaising,
      discountedPurchasePrice: request.discountedPurchasePrice,
      productLengths: request.productLengths,
      rateTypes: request.rateTypes,
      propertyType: request.propertyType,
      applicants: this.getApplicantsFromRequest(request),
      dateOfBirth: this.igniteHelperService.dateToDateInput(request.dateOfBirth),
      customProductLength: { from: request.productLengthFrom, to: request.productLengthTo } as IUiCustomProductLength,
      firstTimeBuyer: request.firstTimeBuyer,
      firstTimeLandlord: request.firstTimeLandlord,
      ownerOccupier: request.ownerOccupier,
      rentalAmountReceivedExpected: request.rentalAmountReceivedExpected,
      location: request.location,
      numberOfBlocks: request.numberOfBlocks,
      numberOfBedrooms: request.numberOfBedrooms,
      holidayLetIncome: {
        holidayLetIncomeLow: request.holidayLetIncomeLow,
        holidayLetIncomeMedium: request.holidayLetIncomeMedium,
        holidayLetIncomeHigh: request.holidayLetIncomeHigh,
        holidayLetIncomeFrequency: request.holidayLetIncomeFrequency,
      } as IHolidayLetIncome,
      numberOfProperties: request.numberOfProperties,
    } as IUiProductsSearchModel['mortgageRequirements'];

    const bridging = {
      regulated: request.regulated,
      propertyValue: request.propertyValue,
      loanAmount: request.loanAmount,
      loanSecurity: request.loanSecurity,
      currentMortgageBalanceOutstanding: request.currentMortgageBalanceOutstanding,
      loanTermMonths: request.loanTerm,
      methodOfRepayment: request.methodOfRepayment,
      limitedCompany: request.limitedCompany,
    } as IBridging;

    return { mortgageRequirements, bridging };
  }

  public loadSavedProducts(productsSearchId: string | undefined): Observable<unknown> {
    if (!productsSearchId) {
      return of();
    }

    this.productsSearchId = productsSearchId;

    this.currentStepName = StepName.Results;
    this.resultsView = ResultsView.MatchedProducts;
    this.loading = true;

    return this.getPreviousOutcome(productsSearchId, true, this.igniteCommonDataService.outcomehistoryId).pipe(
      tap((result: any) => {
        this.lendingTypeService.lendingType = this.lendingTypeService.lendingTypes.find(l => l.id === result.lendingTypeId);
        this.processHistoricProducts(result);
      }),
    );
  }

  public get productTransferMessage(): string {
    if (this.lenderProductTransferViewModel) {
      return this.lenderProductTransferViewModel.infoMessage ?? this.lenderProductTransferViewModel.errorMessage;
    }

    return '';
  }

  public get hasProductTransfer(): boolean {
    return !!this.lenderProductTransferViewModel;
  }

  public get shouldLoadIntuitiveCriteria(): boolean {
    return this.lendingTypeService.isResLendingType || this.lendingTypeService.isBtlLendingType;
  }

  public createPackagerProductClones(matchedLenderProducts: MatchedLenderProducts[]) {
    for (const lenderProducts of matchedLenderProducts) {
      if (!lenderProducts.packagers?.length) {
        continue;
      }

      const packagerClones = lenderProducts.products
        .filter(product => product.isAvailableFromPackagers && !product.packager)
        .flatMap(product => lenderProducts.packagers.map(packager => ({ ...product, packager })));

      lenderProducts.products = [...lenderProducts.products, ...packagerClones];
    }
  }

  public getLenderProductDetails(
    lenderName: string,
    lendingTypeId: number,
    lendingTypeCode: string,
    loanAmount: number | null | undefined,
    product: Product | BridgingProduct,
  ): Observable<LenderProductDetailsViewModel> {
    // Ensure we don't pass an empty string to the endpoint
    if (loanAmount !== null && loanAmount !== undefined && typeof loanAmount !== 'number') {
      loanAmount = null;
    }

    const isBtl = lendingTypeCode?.toLocaleUpperCase() === LendingTypeCode.Btl;
    if (isBtl) {
      return this.productsSearchBtlService.getLenderProductDetails(product as Product, loanAmount ?? null, lenderName, lendingTypeId);
    }

    const isBdg = lendingTypeCode?.toLocaleUpperCase() === LendingTypeCode.Bdg;
    return this.dataService.post<LenderProductDetailsViewModel>(`esisInformation/lender/${lenderName}/${lendingTypeId}`, {
      loanAmount,
      product,
      isBridgingProduct: isBdg,
    });
  }

  public mapModelToFilters() {
    const request = this.productsModelRequest;
    if (!request) {
      return;
    }

    const filters = [] as { groupKey: string; filterKey: string; checked: boolean }[];

    // Clear form filter
    this.productsFilterService.clearGroupFilters(productFiltersNames.initialProductPeriod, productFiltersNames.rateType);

    // "Initial Product Period" filter
    if (request.productLengths?.length) {
      const isInaccuracyReport = this.igniteCommonDataService.isInaccuracyReport;

      if (request.productLengths.includes(ProductLengthExtended.Custom) && !isInaccuracyReport) {
        this.productsFilterService.setRangeValueFilter(
          {
            checked: true,
            excluded: false,
            included: false,
            title: 'Custom (months)',
            fromValue: request.productLengthFrom,
            toValue: request.productLengthTo,
            hasRangeValues: true,
            validationErrors: '',
            onActivateMessage: undefined,
            hasSubFilters: false,
          } as ProductFilter,
          true,
          false,
        );
      } else {
        const initialProductFilterNames = this.productsFilterHelper.getInitialProductPeriodFilters(request.productLengths);

        const initialProductPeriodFilters = initialProductFilterNames.map(filterName => {
          return {
            groupKey: productFiltersNames.initialProductPeriod,
            filterKey: filterName,
            checked: true,
          };
        });
        if (initialProductPeriodFilters.length) {
          filters.push(...initialProductPeriodFilters);
        }
      }
    }

    // "Rate type" filters
    if (request.rateTypes?.length) {
      const rateTypesFilters = request.rateTypes.map(r => ({
        groupKey: productFiltersNames.rateType,
        filterKey: r,
        checked: true,
      }));
      filters.push(...rateTypesFilters);
    }

    if (filters.length) {
      this.productsFilterService.setFilters(filters);
    }
  }

  /**
   * Pushes the sidebar's Initial product period and Rate type selections back onto the search model.
   *
   * Clearing is as much a selection as ticking. These two used to be written only when the sidebar had something
   * in them, so unticking the last one left the model holding the old value: the form still showed it, and a
   * search saved at that point recorded it. Harmless-looking while the model only ever held what the broker had
   * typed, but filter preferences seed these fields, so the value left behind was one they had never chosen.
   *
   * Only ever called from the results page's filterChanged subscription, so an empty group here means the
   * broker has no such filter rather than a sidebar that has not been built yet.
   */
  public mapFilterToModel() {
    if (!this.model) {
      return;
    }

    const productPeriodFilters = this.productsFilterService.currentAppliedFilters.filter(
      f => f.filterGroup === productFiltersNames.initialProductPeriod,
    );

    // 'Initial Product Period' filter setup
    const filterNames = productPeriodFilters.map(f => f.filterName);
    const productLengths = productPeriodFilters.length
      ? this.productsFilterHelper.getRequestModelInitialProductPeriod(filterNames)
      : [];
    this.model.mortgageRequirements.productLengths = productLengths;

    // Set custom range values
    if (productLengths.includes(ProductLengthExtended.Custom)) {
      const customPeriodFilter = productPeriodFilters.find(f => f.filterName === 'Custom (months)');
      this.model.mortgageRequirements.customProductLength = {
        from: customPeriodFilter?.fromValue,
        to: customPeriodFilter?.toValue,
      };
    } else {
      this.model.mortgageRequirements.customProductLength = undefined;
    }

    const rateTypeFilters = this.productsFilterService.currentAppliedFilters.filter(f => f.filterGroup === productFiltersNames.rateType);

    // 'Rate Type' filter setup
    this.model.mortgageRequirements.rateTypes = rateTypeFilters.map(r => r.filterName as ProductTypeString);

    // Apply updated model to the form
    this.form.reset(this.model);
  }

  public getCustomFees() {
    return this.customProductCalculations?.customFees.filter(fee =>
      fee.feeName !== 'Broker Fee' && fee.feeName !== 'Assumed Legal Fee',
    ) ?? [];
  }

  public getBrokerFees(product: Product) {
    const mortgageType = this.productsModelRequest?.mortgageType;

    return this.customProductCalculations?.customFees.filter(fee => {
      const isProductTransfer = this.hasProductTransfer && product.productTransferEligible;
      return fee.feeName === 'Broker Fee' &&
      (
        fee.lendingTypeId === null ||
        this.lendingTypeService.getLendingTypeById(fee.lendingTypeId)?.name === this.lendingTypeService.currentLendingType
      ) &&
      (
        (fee.mortgageTypeProductTransfer && isProductTransfer) ||
        (fee.mortgageTypePurchase && mortgageType === MortgageType.Purchase) ||
        (fee.mortgageTypeRemortgage && mortgageType === MortgageType.Remortgage && !isProductTransfer) ||
        (fee.mortgageTypeSecondCharge && mortgageType === MortgageType.SecondCharge)
      );
    }) ?? [];
  }

  public getAssumedLegalFees(product: Product) {
    if (product.freeLegal !== false) {
      return [];
    }

    const mortgageType = this.productsModelRequest?.mortgageType;
    return this.customProductCalculations?.customFees.filter(fee => {
      return fee.feeName === 'Assumed Legal Fee' &&
      (
        fee.lendingTypeId === null ||
        this.lendingTypeService.getLendingTypeById(fee.lendingTypeId)?.name === this.lendingTypeService.currentLendingType
      ) &&
      (
        (
          fee.mortgageTypeProductTransfer && this.hasProductTransfer && product.productTransferEligible
        ) ||
        (fee.mortgageTypePurchase && mortgageType === MortgageType.Purchase) ||
        (fee.mortgageTypeRemortgage && mortgageType === MortgageType.Remortgage && !product.productTransferEligible) ||
        (fee.mortgageTypeSecondCharge && mortgageType === MortgageType.SecondCharge)
      );
    }) ?? [];
  }
}
